import React, { useState } from 'react';
import { I18nProvider, useI18n } from './core/i18n';
import { ArenaView } from './views/ArenaView';
import { ReplayView } from './views/ReplayView';
import { OnlineView } from './views/OnlineView';
import { PuzzleView } from './views/PuzzleView';
import { AcademyView } from './views/AcademyView';
import { TrackerDrillView } from './views/TrackerDrillView';
import { HandOCRView } from './views/HandOCRView';
import { BaodianView } from './views/BaodianView';
import { BgmPlayer } from './components/Audio/BgmPlayer';
import { BrandLogo } from './components/Logo/BrandLogo';
import { PlayerProfileModal } from './components/Profile/PlayerProfileModal';
import { CardBackCustomizerModal } from './components/Card/CardBackCustomizerModal';
import { UserProfile, loadUserProfile, getExpProgress } from './core/profile';
import { ReplayRecord } from './core/types';
import {
  Swords,
  Trophy,
  BookOpen,
  Brain,
  Camera,
  Film,
  Globe,
  Info,
  X,
  ShieldCheck,
  GraduationCap,
  Sparkles,
  Scroll,
  Palette,
} from 'lucide-react';

type TabType = 'arena' | 'replay' | 'online' | 'puzzles' | 'drills' | 'baodian' | 'academy' | 'ocr';

const AppContent: React.FC = () => {
  const { t, locale, setLocale } = useI18n();
  const [currentTab, setCurrentTab] = useState<TabType>('arena');
  const [activeReplayRecord, setActiveReplayRecord] = useState<ReplayRecord | null>(null);
  const [showAboutModal, setShowAboutModal] = useState<boolean>(false);
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [showCardBackModal, setShowCardBackModal] = useState<boolean>(false);
  const [userProfile, setUserProfile] = useState<UserProfile>(() => loadUserProfile());

  const expProgress = getExpProgress(userProfile.exp);

  const handleNavigateToReplay = (record: ReplayRecord) => {
    setActiveReplayRecord(record);
    setCurrentTab('replay');
  };

  const handleLaunchSwapMatchFromReplay = () => {
    setCurrentTab('arena');
  };

  return (
    <div className="h-[100dvh] w-[100dvw] max-h-[100dvh] max-w-[100dvw] overflow-hidden bg-slate-950 text-slate-100 flex flex-col selection:bg-amber-500 selection:text-slate-950 font-sans">
      {/* Compact Top Navbar */}
      <header className="shrink-0 h-10 sm:h-12 landscape:h-8.5 bg-slate-950/90 backdrop-blur-md border-b border-slate-800/80 shadow-md z-40">
        <div className="h-full w-full max-w-7xl mx-auto px-2 sm:px-4 flex items-center justify-between">
          {/* Logo & Brand Header */}
          <div
            className="flex items-center space-x-1.5 sm:space-x-2 cursor-pointer select-none group"
            onClick={() => setCurrentTab('arena')}
          >
            <BrandLogo size="sm" />

            <div className="flex flex-col">
              <div className="text-xs sm:text-sm font-black tracking-tight text-white flex items-center gap-1.5 leading-none">
                <span>{t.appName}</span>
                {import.meta.env.VITE_APP_ENV === 'beta' ? (
                  <span className="text-[8px] sm:text-[9px] bg-gradient-to-r from-rose-500 to-amber-500 text-white px-1.5 py-0.2 rounded font-black shadow animate-pulse">
                    ⚡ BETA 体验版
                  </span>
                ) : (
                  <span className="text-[8px] sm:text-[9px] bg-gradient-to-r from-amber-500/20 to-sky-500/20 text-amber-300 px-1 py-0.1 rounded font-bold border border-amber-500/30">
                    {t.appBadge}
                  </span>
                )}
              </div>
              <div className="text-[8px] sm:text-[9px] text-slate-400 font-medium leading-tight flex items-center gap-1 mt-0.5">
                <span className="text-amber-400/90 font-semibold">{t.brandName}</span>
                <span className="text-slate-600">·</span>
                <span className="hidden lg:inline text-sky-400/90">{t.zjuAgent}</span>
                <span className="hidden lg:inline text-slate-600">·</span>
                <span className="hidden sm:inline text-slate-400">{t.naAlumni}</span>
              </div>
            </div>
          </div>

          {/* Desktop Navigation Tabs */}
          <nav className="hidden md:flex items-center space-x-0.5 sm:space-x-1 bg-slate-900/90 p-0.5 sm:p-1 rounded-xl border border-slate-800">
            <button
              onClick={() => setCurrentTab('arena')}
              className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-extrabold transition-all ${
                currentTab === 'arena'
                  ? 'bg-amber-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Swords className="w-3.5 h-3.5" />
              <span>{t.tabArena}</span>
            </button>

            <button
              onClick={() => setCurrentTab('replay')}
              className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-extrabold transition-all ${
                currentTab === 'replay'
                  ? 'bg-sky-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Film className="w-3.5 h-3.5" />
              <span>{t.tabReplay}</span>
            </button>

            <button
              onClick={() => setCurrentTab('online')}
              className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-extrabold transition-all ${
                currentTab === 'online'
                  ? 'bg-emerald-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Globe className="w-3.5 h-3.5" />
              <span>{t.tabOnline}</span>
            </button>

            <button
              onClick={() => setCurrentTab('baodian')}
              className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-extrabold transition-all ${
                currentTab === 'baodian'
                  ? 'bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 shadow-sm'
                  : 'text-amber-300 hover:text-white'
              }`}
            >
              <Scroll className="w-3.5 h-3.5" />
              <span>宝典</span>
            </button>

            <button
              onClick={() => setCurrentTab('puzzles')}
              className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-extrabold transition-all ${
                currentTab === 'puzzles'
                  ? 'bg-purple-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Brain className="w-3.5 h-3.5" />
              <span>{t.tabPuzzles}</span>
            </button>

            <button
              onClick={() => setCurrentTab('drills')}
              className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-extrabold transition-all ${
                currentTab === 'drills'
                  ? 'bg-indigo-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Trophy className="w-3.5 h-3.5" />
              <span>{t.tabDrills}</span>
            </button>

            <button
              onClick={() => setCurrentTab('academy')}
              className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-extrabold transition-all ${
                currentTab === 'academy'
                  ? 'bg-teal-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>{t.tabAcademy}</span>
            </button>

            <button
              onClick={() => setCurrentTab('ocr')}
              className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-extrabold transition-all ${
                currentTab === 'ocr'
                  ? 'bg-rose-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Camera className="w-3.5 h-3.5" />
              <span>{t.tabOcr}</span>
            </button>
          </nav>

          {/* Right Area: Profile / BGM / Lang / Info */}
          <div className="flex items-center space-x-1 sm:space-x-2">
            {/* User Level & Profile Badge Button */}
            <button
              onClick={() => setShowProfileModal(true)}
              className="bg-gradient-to-r from-amber-500/20 via-amber-400/15 to-sky-500/20 hover:from-amber-500/30 border border-amber-500/40 rounded-xl px-2 py-1 flex items-center space-x-1.5 transition-transform active:scale-95 shadow"
              title="查看段位荣耀、战绩雷达与云同步"
            >
              <div className="w-5 h-5 bg-gradient-to-tr from-amber-400 to-amber-600 rounded-lg flex items-center justify-center text-slate-950 font-black text-[10px] shadow">
                {expProgress.tier.icon}
              </div>
              <div className="flex flex-col items-start leading-none text-left">
                <span className="text-[10px] font-black text-amber-300">{expProgress.tier.name}</span>
                <span className="text-[8px] text-slate-400 font-mono scale-90 -ml-0.5">
                  Lv.{userProfile.level} EXP {expProgress.percent}%
                </span>
              </div>
            </button>

            {/* Suno BGM Player */}
            <BgmPlayer />

            {/* Language Selector */}
            <div className="hidden sm:flex items-center bg-slate-900 border border-slate-800 rounded-lg p-0.5 text-[10px] font-bold">
              <button
                onClick={() => setLocale('zh_cn')}
                className={`px-1.5 py-0.5 rounded transition-all ${
                  locale === 'zh_cn' ? 'bg-amber-500 text-slate-950 shadow' : 'text-slate-400 hover:text-white'
                }`}
                title="简体中文"
              >
                简
              </button>
              <button
                onClick={() => setLocale('zh_tw')}
                className={`px-1.5 py-0.5 rounded transition-all ${
                  locale === 'zh_tw' ? 'bg-amber-500 text-slate-950 shadow' : 'text-slate-400 hover:text-white'
                }`}
                title="繁體中文"
              >
                繁
              </button>
              <button
                onClick={() => setLocale('en')}
                className={`px-1.5 py-0.5 rounded transition-all ${
                  locale === 'en' ? 'bg-amber-500 text-slate-950 shadow' : 'text-slate-400 hover:text-white'
                }`}
                title="English"
              >
                EN
              </button>
            </div>

            {/* Card Back Customizer Button */}
            <button
              onClick={() => setShowCardBackModal(true)}
              className="bg-slate-900/80 hover:bg-slate-800 text-amber-300 hover:text-amber-200 px-2 py-1 rounded-lg border border-amber-500/40 text-[11px] font-bold flex items-center gap-1 transition-transform active:scale-95 shadow"
              title="定制战队牌背与专属Logo"
            >
              <Palette className="w-3.5 h-3.5 text-amber-400" />
              <span className="hidden sm:inline">定制牌背</span>
            </button>

            {/* Cross-Environment Link Button (Prod <-> Beta) */}
            {import.meta.env.VITE_APP_ENV === 'beta' ? (
              <a
                href="https://guandan.weiai.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-emerald-950/90 hover:bg-emerald-900 text-emerald-300 hover:text-emerald-200 px-2 py-1 rounded-lg border border-emerald-500/60 text-[11px] font-black flex items-center gap-1 transition-all shadow active:scale-95"
                title="前往生产稳定版 (guandan.weiai.ai)"
              >
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="hidden sm:inline">稳定版</span>
                <span className="sm:hidden">Prod</span>
              </a>
            ) : (
              <a
                href="https://aiguandan-beta.pages.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-rose-500/20 via-amber-500/20 to-amber-600/30 hover:from-rose-500/30 hover:to-amber-500/40 text-amber-300 border border-amber-500/50 px-2 py-1 rounded-lg text-[11px] font-black flex items-center gap-1 transition-all shadow active:scale-95"
                title="前往先锋体验版 (Beta)，体验最新特性"
              >
                <Sparkles className="w-3 h-3 text-amber-400 animate-spin" />
                <span className="hidden sm:inline">⚡体验版</span>
                <span className="sm:hidden">Beta</span>
              </a>
            )}

            <button
              onClick={() => setShowAboutModal(true)}
              className="bg-slate-900/80 hover:bg-slate-800 text-slate-300 hover:text-white px-2 py-1 rounded-lg border border-slate-700 text-[11px] font-bold flex items-center gap-1 transition-transform active:scale-95 shadow"
              title={t.aboutBtn}
            >
              <Info className="w-3.5 h-3.5 text-amber-400" />
              <span className="hidden sm:inline">{t.aboutBtn}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Dynamically-Fitting Workspace View */}
      <main className="flex-1 min-h-0 w-full max-w-7xl mx-auto p-1 sm:p-2 overflow-hidden flex flex-col">
        {currentTab === 'arena' && <ArenaView onNavigateToReplay={handleNavigateToReplay} />}
        {currentTab === 'replay' && (
          <ReplayView
            initialRecord={activeReplayRecord}
            onLaunchSwapMatch={handleLaunchSwapMatchFromReplay}
          />
        )}
        {currentTab === 'online' && <OnlineView />}
        {currentTab === 'baodian' && <BaodianView />}
        {currentTab === 'puzzles' && <PuzzleView />}
        {currentTab === 'drills' && <TrackerDrillView />}
        {currentTab === 'academy' && <AcademyView />}
        {currentTab === 'ocr' && <HandOCRView />}
      </main>

      {/* Mobile Bottom Navigation Bar (Only in Portrait mode) */}
      <nav className="md:hidden portrait:flex landscape:hidden shrink-0 bg-slate-950/95 backdrop-blur-md border-t border-slate-800 px-2 py-0.5 flex justify-around z-40">
        <button
          onClick={() => setCurrentTab('arena')}
          className={`flex flex-col items-center p-1 text-[9px] font-bold ${
            currentTab === 'arena' ? 'text-amber-400' : 'text-slate-400'
          }`}
        >
          <Swords className="w-3.5 h-3.5 mb-0.5" />
          <span>{t.tabArena}</span>
        </button>
        <button
          onClick={() => setCurrentTab('baodian')}
          className={`flex flex-col items-center p-1 text-[9px] font-bold ${
            currentTab === 'baodian' ? 'text-amber-300 font-extrabold' : 'text-slate-400'
          }`}
        >
          <Scroll className="w-3.5 h-3.5 mb-0.5" />
          <span>宝典</span>
        </button>
        <button
          onClick={() => setCurrentTab('replay')}
          className={`flex flex-col items-center p-1 text-[9px] font-bold ${
            currentTab === 'replay' ? 'text-sky-400' : 'text-slate-400'
          }`}
        >
          <Film className="w-3.5 h-3.5 mb-0.5" />
          <span>{t.tabReplay}</span>
        </button>
        <button
          onClick={() => setCurrentTab('online')}
          className={`flex flex-col items-center p-1 text-[9px] font-bold ${
            currentTab === 'online' ? 'text-emerald-400' : 'text-slate-400'
          }`}
        >
          <Globe className="w-3.5 h-3.5 mb-0.5" />
          <span>{t.tabOnline}</span>
        </button>
        <button
          onClick={() => setCurrentTab('puzzles')}
          className={`flex flex-col items-center p-1 text-[9px] font-bold ${
            currentTab === 'puzzles' ? 'text-purple-400' : 'text-slate-400'
          }`}
        >
          <Brain className="w-3.5 h-3.5 mb-0.5" />
          <span>{t.tabPuzzles}</span>
        </button>
        <button
          onClick={() => setCurrentTab('drills')}
          className={`flex flex-col items-center p-1 text-[9px] font-bold ${
            currentTab === 'drills' ? 'text-indigo-400' : 'text-slate-400'
          }`}
        >
          <Trophy className="w-3.5 h-3.5 mb-0.5" />
          <span>五十定律</span>
        </button>
        <button
          onClick={() => setCurrentTab('academy')}
          className={`flex flex-col items-center p-1 text-[9px] font-bold ${
            currentTab === 'academy' ? 'text-teal-400' : 'text-slate-400'
          }`}
        >
          <GraduationCap className="w-3.5 h-3.5 mb-0.5" />
          <span>学院</span>
        </button>
      </nav>

      {/* About & Credits Modal */}
      {showAboutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in font-sans">
          <div className="bg-slate-900 border border-slate-700/80 rounded-2xl max-w-md w-full p-5 shadow-2xl space-y-4 relative">
            <button
              onClick={() => setShowAboutModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center space-x-3">
              <BrandLogo size="md" />
              <div>
                <h3 className="text-base font-black text-white">{t.appName}</h3>
                <p className="text-xs text-amber-400 font-bold">{t.aboutTitle}</p>
              </div>
            </div>

            <div className="space-y-2 text-xs text-slate-300 leading-relaxed border-t border-slate-800 pt-3">
              <p>{t.aboutInst1Desc}</p>

              {/* Special Credits */}
              <div className="bg-slate-950 p-2.5 rounded-xl border border-slate-800 space-y-1.5">
                <div className="text-xs font-black text-amber-300 flex items-center gap-1">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>经典宝典与名宿实战经验致谢</span>
                </div>
                <ul className="text-[11px] text-slate-400 space-y-0.5 list-disc list-inside">
                  <li><strong>广州市中山大学校友会</strong>：传承收录《掼蛋宝典》（18条黄金实战口诀原件与精解）</li>
                  <li><strong>蒋主席</strong>（北美高校联盟掼蛋俱乐部名誉主席）：口述实录《掼蛋实战宝典与博弈心法》（残局逆向预设、困死孤张、配角拯救大兵、50%过牌法则）</li>
                  <li><strong>慧姐</strong>（北美高校联盟北加硅谷名宿）：口述实录《实战牌语与攻防心法》（首发小单强牌、对子探路、多炸主攻单炸僚机）</li>
                </ul>
              </div>

              {/* Creator Credit */}
              <div className="text-[11px] text-slate-500 border-t border-slate-800/80 pt-2 flex items-center justify-between">
                <span>{t.instructor}</span>
                <span className="text-amber-400/80">guandan.weiai.ai</span>
              </div>
            </div>

            <button
              onClick={() => setShowAboutModal(false)}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black py-2 rounded-xl text-xs shadow transition-transform active:scale-95"
            >
              {t.aboutReturn}
            </button>
          </div>
        </div>
      )}

      {/* User Profile & EXP Modal */}
      {showProfileModal && (
        <PlayerProfileModal
          profile={userProfile}
          onUpdateProfile={setUserProfile}
          onClose={() => setShowProfileModal(false)}
        />
      )}

      {/* Card Back & Logo Customizer Modal */}
      <CardBackCustomizerModal
        isOpen={showCardBackModal}
        onClose={() => setShowCardBackModal(false)}
      />
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <I18nProvider>
      <AppContent />
    </I18nProvider>
  );
};
